import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';

const VerifiedBankAccountFields = ({ value = {}, onChange }) => {
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const lastVerifiedRef = useRef('');

  useEffect(() => {
    const loadBanks = async () => {
      setLoadingBanks(true);
      try {
        const response = await fetch(`${API_ENDPOINTS.SALARIES}/banks`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setBanks(data
            .map((bank) => ({ ...bank, name: (bank.name || '').trim() }))
            .sort((a, b) => a.name.localeCompare(b.name)));
        }
      } catch (error) {
        console.error('Error loading banks:', error);
      } finally {
        setLoadingBanks(false);
      }
    };

    loadBanks();
  }, []);

  const selectedBankCode = value.bankCode || value.code || '';
  const selectedBank = useMemo(() => {
    if (selectedBankCode) {
      return banks.find((bank) => bank.code === selectedBankCode || bank.id === selectedBankCode);
    }

    const savedBankName = (value.bankName || '').toLowerCase().trim();
    if (!savedBankName) return null;
    return banks.find((bank) => bank.name.toLowerCase() === savedBankName) || null;
  }, [banks, selectedBankCode, value.bankName]);

  const filteredBanks = useMemo(() => {
    const search = bankSearch.toLowerCase().trim();
    if (!search) return banks;

    return banks.filter((bank) => (
      bank.name.toLowerCase().includes(search) ||
      bank.code.toLowerCase().includes(search)
    ));
  }, [banks, bankSearch]);

  const bankOptions = useMemo(() => {
    if (!selectedBank || filteredBanks.some((bank) => bank.code === selectedBank.code)) {
      return filteredBanks;
    }

    return [selectedBank, ...filteredBanks];
  }, [filteredBanks, selectedBank]);

  const updateBankAccount = (updates) => {
    onChange({
      ...value,
      ...updates
    });
  };

  const clearVerification = (updates) => {
    updateBankAccount({
      ...updates,
      accountName: '',
      verified: false,
      verifiedAt: ''
    });
    setMessage('');
  };

  const verifyAccount = useCallback(async ({ automatic = false } = {}) => {
    const accountNumber = (value.accountNumber || '').toString().trim();
    const signature = `${selectedBank?.code || ''}:${accountNumber}`;

    if (!selectedBank?.code || !accountNumber) {
      if (!automatic) setMessage('Select a bank and enter a 10-digit account number.');
      return;
    }
    if (!/^\d{10}$/.test(accountNumber)) {
      if (!automatic) setMessage('Enter a valid 10-digit account number.');
      return;
    }
    if (automatic && (verifying || value.verified || lastVerifiedRef.current === signature)) return;

    setVerifying(true);
    setMessage(automatic ? 'Verifying account...' : '');
    try {
      const response = await fetch(`${API_ENDPOINTS.SALARIES}/verify-bank-account`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bankCode: selectedBank.code,
          accountNumber
        })
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMessage(result.details || result.error || 'Account verification failed.');
        return;
      }

      updateBankAccount({
        accountName: result.account.accountName || '',
        bankName: result.account.bankName || selectedBank.name,
        bankCode: result.account.bankCode || selectedBank.code,
        bankId: result.account.bankId || selectedBank.id,
        accountNumber: result.account.accountNumber || accountNumber,
        verified: true,
        verifiedAt: new Date().toISOString()
      });
      lastVerifiedRef.current = signature;
      setMessage(`Verified: ${result.account.accountName}`);
    } catch (error) {
      console.error('Error verifying bank account:', error);
      setMessage('Unable to verify account right now.');
    } finally {
      setVerifying(false);
    }
  }, [onChange, selectedBank, value, verifying]);

  useEffect(() => {
    const accountNumber = (value.accountNumber || '').toString().trim();
    if (!selectedBank?.code || !/^\d{10}$/.test(accountNumber) || value.verified) return undefined;
    const timer = setTimeout(() => {
      verifyAccount({ automatic: true });
    }, 450);
    return () => clearTimeout(timer);
  }, [selectedBank?.code, value.accountNumber, value.verified, verifyAccount]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Search Bank</label>
          <input
            type="text"
            value={bankSearch}
            onChange={(e) => setBankSearch(e.target.value)}
            className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Type bank name or code"
            disabled={loadingBanks}
          />
          <select
            value={selectedBank?.code || ''}
            onChange={(e) => {
              const bank = banks.find((item) => item.code === e.target.value);
              clearVerification({
                bankName: bank?.name || '',
                bankCode: bank?.code || '',
                bankId: bank?.id || ''
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loadingBanks}
          >
            <option value="">{loadingBanks ? 'Loading banks...' : 'Select bank'}</option>
            {bankOptions.map((bank) => (
              <option key={`${bank.code}-${bank.id}`} value={bank.code}>
                {bank.name} ({bank.code})
              </option>
            ))}
          </select>
          {!loadingBanks && bankSearch && filteredBanks.length === 0 && (
            <p className="text-xs text-red-600 mt-1">No matching bank found.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
          <input
            type="text"
            value={value.accountNumber || ''}
            onChange={(e) => clearVerification({ accountNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="10 digits"
            inputMode="numeric"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Verified Account Name</label>
          <input
            type="text"
            value={value.accountName || ''}
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
            placeholder="Verify to fill name"
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <button
          type="button"
          onClick={verifyAccount}
          disabled={verifying || !selectedBank?.code || (value.accountNumber || '').length !== 10}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
        >
          {verifying ? 'Verifying...' : 'Verify Account'}
        </button>
        {value.verified && value.accountName && (
          <span className="text-sm font-medium text-green-700">Verified account ready for payouts</span>
        )}
        {message && (
          <span className={`text-sm ${value.verified ? 'text-green-700' : 'text-red-700'}`}>{message}</span>
        )}
      </div>
    </div>
  );
};

export default VerifiedBankAccountFields;
